import React from 'react';
import { Link } from 'react-router-dom';
import {
    Button,
    Container,
    Typography,
    Box,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Paper, // Added Paper for feature cards
    Grid // Added Grid for layout
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'; // Heart outline icon
import PersonIcon from '@mui/icons-material/Person'; // User profile icon
import ShowChartIcon from '@mui/icons-material/ShowChart'; // Upward-trending line graph icon
import StarBorderIcon from '@mui/icons-material/StarBorder'; // Star for sparkle (can be replaced if a better icon is found)
import KeyIcon from '@mui/icons-material/Key'; // Keyhole icon
import LanguageIcon from '@mui/icons-material/Language'; // Globe icon
import { useTranslation } from 'react-i18next';

const Home = () => {
    const { t, i18n } = useTranslation();

    const changeLanguage = (event) => {
        i18n.changeLanguage(event.target.value);
    };

    const FeatureCard = ({ icon, title, description, iconBgColor }) => (
        <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: '16px', textAlign: 'center', height: '100%' }}>
                <Box
                    sx={{
                        bgcolor: iconBgColor,
                        borderRadius: '50%',
                        width: 60,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                    }}
                >
                    {icon}
                </Box>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </Paper>
        </Grid>
    );

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                bgcolor: '#f8fcf8', // Very light, off-white
                background: 'linear-gradient(to bottom right, #e8f5e9, #ffffff)', // Subtle green-white gradient
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                }}
            >
                {/* Left Header - Logo and AKIDSCARE Text */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            bgcolor: '#00bcd4', // Teal
                            borderRadius: '8px',
                            p: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <FavoriteBorderIcon sx={{ color: 'white', fontSize: '20px' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {t('akidscare_title')}
                    </Typography>
                </Box>

                {/* Right Header - Language Selector */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LanguageIcon sx={{ color: 'text.secondary' }} />
                    <FormControl variant="standard" sx={{ minWidth: 60 }}>
                        <Select
                            value={i18n.language}
                            onChange={changeLanguage}
                            disableUnderline
                            inputProps={{ 'aria-label': 'Without label' }}
                            sx={{ '.MuiSelect-select': { p: 0, pr: '24px !important' } }}
                        >
                            <MenuItem value="en">EN</MenuItem>
                            <MenuItem value="te">TE</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Container maxWidth="lg" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: { xs: 2, md: 3 } }}>
                {/* Hero Section */}
                <FavoriteBorderIcon sx={{ color: '#00bcd4', fontSize: { xs: 60, md: 80 }, mb: 1 }} />
                <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: { xs: '2rem', md: '3rem' } }}>
                    {t('welcome_to_akidscare')}
                </Typography>
                <Typography variant="h6" component="h2" color="text.secondary" sx={{ mb: { xs: 2, md: 4 }, textAlign: 'center', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    {t('supporting_child_development')}
                </Typography>

                {/* Feature Cards */}
                <Grid container spacing={{ xs: 2, md: 3 }} justifyContent="center" sx={{ mb: { xs: 3, md: 4 } }}>
                    <FeatureCard
                        icon={<PersonIcon sx={{ color: 'white', fontSize: 30 }} />}
                        title={t('feature_card_1_title')}
                        description={t('feature_card_1_description')}
                        iconBgColor="#4fc3f7" // Light blue
                    />
                    <FeatureCard
                        icon={<ShowChartIcon sx={{ color: 'white', fontSize: 30 }} />}
                        title={t('feature_card_2_title')}
                        description={t('feature_card_2_description')}
                        iconBgColor="#81c784" // Light green
                    />
                    <FeatureCard
                        icon={<StarBorderIcon sx={{ color: 'white', fontSize: 30 }} />} // Using StarBorder for sparkle
                        title={t('feature_card_3_title')}
                        description={t('feature_card_3_description')}
                        iconBgColor="#ba68c8" // Light purple
                    />
                    <FeatureCard
                        icon={<KeyIcon sx={{ color: 'white', fontSize: 30 }} />}
                        title={t('feature_card_4_title')}
                        description={t('feature_card_4_description')}
                        iconBgColor="#fff176" // Light yellow
                    />
                </Grid>

                {/* Call to Action */}
                <Button
                    variant="contained"
                    color="primary"
                    component={Link}
                    to="/login"
                    sx={{
                        bgcolor: '#00bcd4', // Teal
                        borderRadius: '8px',
                        py: 1.5,
                        px: 4,
                        fontSize: '1.1rem',
                        '&:hover': {
                            bgcolor: '#00acc1', // Darker teal on hover
                        },
                        mb: 2,
                    }}
                >
                    {t('login_to_your_account')}
                </Button>
                {/* <Link to="/register" style={{ textDecoration: 'none' }}>
                    <Typography variant="body1" color="primary">
                        {t('get_started_today')}
                    </Typography>
                </Link> */}
            </Container>
        </Box>
    );
};

export default Home;
